import HomeOutlined from "@ant-design/icons/HomeOutlined";
import { getRoleFromPermission } from "@sdk/roles-and-permissions/roles-and-permissions";
import {
  Alert,
  Button,
  Divider,
  Form,
  Input,
  Layout,
  Typography,
  message,
} from "antd";
import { Avatar } from "components/common/avatar/avatar";
import { LoadingIndicatorWithSpin } from "components/common/loading-indicator/loading-indicator";
import { SignInWithGoogle } from "components/common/sign-in-with-google/sign-in-with-google";
import { useCallback, useEffect, useMemo, useState } from "react";
import OtpInput from "react-otp-input";
import { useDispatch, useSelector } from "react-redux";
import {
  addRecentLoginData,
  removeRecentLoginData,
} from "store/modules/app-state/app-state.actions";
import { iRecentLoginData } from "store/modules/app-state/app-state.model";
import { selectRecentLogins } from "store/modules/app-state/app-state.selectors";
import {
  setAccessToken,
  setCurrentUser,
} from "store/modules/users/users.actions";
import classNames from "utils/classnames";
import { justWait } from "utils/just-wait";
import { useSDKActionWithDeps } from "../../../@sdk/sdk.hooks";
import "./signin.page.style.scss";

const { Footer } = Layout;
const { Title } = Typography;

const SignInPage = () => {
  const dispatch = useDispatch();

  const [email, setEmail] = useState("");
  const [userAccessToken, setUserAccessToken] = useState("");
  const [accounts, setAccounts] = useState(
    [] as {
      id: string;
      workspaceId: string;
      workspaceLogo: string;
      workspaceName: string;
      workspaceDomain: string;
      workspaceActive: string;
      userAvatar: string;
      userEmail: string;
      userName: string;
    }[],
  );

  const switchProfile = useCallback((profileData: iRecentLoginData) => {
    window.location.href = `https://${profileData.domain.toLowerCase()}.clickconnector.io/login`;
  }, []);

  const removeProfile = useCallback(
    (profileData: iRecentLoginData) => {
      dispatch(
        removeRecentLoginData({
          email: profileData.email,
          domain: profileData.domain,
        }),
      );
    },
    [dispatch],
  );

  const _recentLogins = useSelector(selectRecentLogins);

  const recentLogins = useMemo(() => _recentLogins.slice(-2), [_recentLogins]);

  const [step, setStep] = useState<
    "LOGIN" | "OTP" | "SELECT_ACCOUNT" | "SELECT_WORKSPACE"
  >("LOGIN");
  const [otp, setOtp] = useState("");

  const loginWithGoogle = useCallback(() => {
    const clientId =
      "504090714122-mku64i7e0f2dv28jbq64uv6tun6tqe1h.apps.googleusercontent.com";
    const redirectUri =
      "https://auth.clickconnector.io/external-auth-connector";
    // const redirectUri = "http://localhost:3001/signin";

    const scopes = ["email"];
    const url = `https://accounts.google.com/o/oauth2/v2/auth?access_type=offline&scope=${encodeURIComponent(
      scopes.join(" "),
    )}&include_granted_scopes=true&prompt=consent&response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}`;

    window.open(url, "cc-auth-connector");
  }, []);

  const { doAction: getOtp, isProcessing } = useSDKActionWithDeps(
    () => ({
      action: (SDK) => (email: string) =>
        SDK.getOtp(email).then((res) => {
          setEmail(email);
          setStep("OTP");
          return res;
        }),
      successMessage: "OTP has been sent",
      failureMessage: "Something went wrong",
    }),
    [],
  );

  const { doAction: validateWorkspace, isProcessing: isValidatingWorkspace } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => (domain: string) =>
          SDK.checkDomainAvailability(domain).then((res) => {
            if (res.availability) {
              message.error("Invalid Workspace Domain");
            } else {
              window.location.href = `https://${domain.toLowerCase()}.clickconnector.io/login`;
            }
            return res;
          }),
        // successMessage: "OTP has been sent",
        failureMessage: "Something went wrong",
      }),
      [],
    );

  const {
    doAction: getAccountsWithUserAccessToken,
    isProcessing: isFetchingAccounts,
  } = useSDKActionWithDeps(
    () => ({
      action: (SDK) => (accessToken: string) =>
        SDK.getAccountsWithUserAccessToken(accessToken).then((res) => {
          setAccounts(res);
          return res;
        }),
      // successMessage: "OTP has been sent",
      failureMessage: "Something went wrong",
    }),
    [],
  );

  const {
    doAction: getTokenWithGoogleCode,
    isProcessing: isGeneratingTokenWithGoogle,
  } = useSDKActionWithDeps(
    () => ({
      action: (SDK) => (code: string) =>
        SDK.getTokenWithGoogleCode(code).then((res) => {
          setUserAccessToken(res.userAccessToken);
          setEmail(res.email);
          setStep("SELECT_ACCOUNT");
          getAccountsWithUserAccessToken(res.userAccessToken);
          return res;
        }),
      // successMessage: "OTP has been sent",
      failureMessage: "Something went wrong",
    }),
    [getAccountsWithUserAccessToken],
  );

  const onGoogleAuthenticated = useCallback(
    (data: { code: string }) => {
      // console.log("data", data);
      getTokenWithGoogleCode(data.code);
    },
    [getTokenWithGoogleCode],
  );

  useEffect(() => {
    const receiveMessage = (event) => {
      if (event?.data?.externalAuthConnector) {
        onGoogleAuthenticated(event?.data?.externalAuthConnector);
      }
    };
    window.addEventListener("message", receiveMessage, false);
    return () => {
      window.removeEventListener("message", receiveMessage);
    };
  }, [onGoogleAuthenticated]);

  const { doAction: getTokenWithOTP, isProcessing: isGeneratingToken } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => (email: string, otp: string) =>
          SDK.getTokenWithOTP(email, otp).then((res) => {
            setUserAccessToken(res.userAccessToken);
            setStep("SELECT_ACCOUNT");
            getAccountsWithUserAccessToken(res.userAccessToken);
            return res;
          }),
        // successMessage: "OTP has been sent",
        failureMessage: "Something went wrong",
      }),
      [getAccountsWithUserAccessToken],
    );

  useEffect(() => {
    if (otp.length === 6) {
      getTokenWithOTP(email, otp);
    }
  }, [email, getTokenWithOTP, otp]);

  const { doAction: loginWithUserToken, isProcessing: isLogginIn } =
    useSDKActionWithDeps(
      () => ({
        action:
          (SDK) =>
          (
            userAccessToken: string,
            organizationId: string,
            domain: string,
            email: string,
          ) =>
            SDK.loginWithUserToken(userAccessToken, organizationId)
              .then((res) => {
                const { user, access } = res;
                // Validate IP
                const role = getRoleFromPermission(user.permissions);
                if (role !== "Owner") {
                  if (!access.valid) {
                    throw {
                      response: {
                        data: {
                          message:
                            "Your IP has been blocked from accessing portal",
                        },
                      },
                    };
                  }
                }
                return res;
              })
              .then((res) => {
                const enableSubDomains =
                  !window.location.href.includes("localhost") &&
                  !window.location.href.includes("vercel.app");
                if (enableSubDomains) {
                  dispatch(
                    addRecentLoginData({
                      // Org Data
                      domain: domain.toLowerCase(),
                      // organizationLogo: string,
                      // User Data
                      email: email.toLowerCase(),
                      firstName: res.user.data.firstName,
                      lastName: res.user.data.lastName,
                      avatar: res.user.data.avatar,
                      // Access Data
                      accessToken: "N/A",
                    }),
                  );
                  window.location.href = `https://${domain.toLowerCase()}.clickconnector.io/login?accessToken=${
                    res.accessToken
                  }`;
                  return justWait(4000);
                } else {
                  SDK.configure({ token: res.accessToken });
                  dispatch(setCurrentUser(res.user));
                  dispatch(setAccessToken(res.accessToken));
                  dispatch(
                    addRecentLoginData({
                      // Org Data
                      domain: domain.toLowerCase(),
                      // organizationLogo: string,
                      // User Data
                      email: email.toLowerCase(),
                      firstName: res.user.data.firstName,
                      lastName: res.user.data.lastName,
                      avatar: res.user.data.avatar,
                      // Access Data
                      accessToken: "N/A",
                    }),
                  );
                }
                return res;
              })
              .catch((e) => {
                console.log("e", e);
                throw e;
              }),
        successMessage: "Successfully logged in",
        failureMessage: "Something went wrong",
      }),
      [dispatch],
    );

  if (step === "OTP") {
    return (
      <Layout className="layout h-full w-full flex flex-col justify-center items-center login-page">
        <img
          src="/cc-assets/logo.png"
          style={{ width: "5rem" }}
          alt="ClickConnector"
        />
        <div
          className="mt-4 bg-white dark:bg-gray-900 p-12 rounded-2xl  mode_transition text-center"
          style={{ maxWidth: 600 }}
        >
          <Title level={1}>Check your Email for a code</Title>

          <div className="text-base">
            We’ve sent a 6-character code to <b>{email}</b>. The code expires
            shortly, so please enter it soon.
          </div>

          <div className="my-8 flex flex-row justify-center items-center">
            <OtpInput
              className="otp-input"
              value={otp}
              onChange={setOtp}
              numInputs={6}
              separator={<span>-</span>}
              shouldAutoFocus
            />
          </div>

          <Button
            type="primary"
            className="font-bold w-full"
            loading={isGeneratingToken}
            size="large"
            icon={<i className="ri-arrow-right-line"></i>}
            onClick={() => {
              getTokenWithOTP(email, otp);
            }}
          >
            Continue
          </Button>

          <div className="text-gray-600 dark:text-gray-200 text-base mt-4">
            Can’t find your code? Check your spam folder!
          </div>
        </div>
        <Footer style={{ textAlign: "center" }}>
          ClickConnector ©{new Date().getFullYear()}
        </Footer>
      </Layout>
    );
  }

  if (step === "SELECT_ACCOUNT" && !isLogginIn) {
    return (
      <Layout className="layout h-full w-full flex flex-col justify-center items-center">
        <img
          src="/cc-assets/logo.png"
          style={{ width: "5rem" }}
          alt="ClickConnector"
        />
        <div className="mt-4 bg-white dark:bg-gray-900 p-12 rounded-2xl  mode_transition">
          <Title level={2}>Welcome back! Stay hydrated!</Title>
          <div className="text-base text-gray-600 dark:text-gray-400">
            Choose a workspace below to get back to working with your team.
          </div>
          <div className="my-8">
            <div className="mb-2">
              Workspaces for <b>{email}</b>
            </div>
            {isFetchingAccounts && (
              <div className="w-full flex flex-row justify-center items-center">
                <LoadingIndicatorWithSpin />
              </div>
            )}
            {!isFetchingAccounts && accounts.length === 0 && (
              <div>
                <Alert
                  type="warning"
                  showIcon
                  message="No workspaces found"
                  description={`It looks like there are no accounts associated with ${email}`}
                />
              </div>
            )}
            {accounts.length > 0 &&
              accounts.map((item, index) => (
                <div
                  key={index.toString()}
                  onClick={() => {
                    loginWithUserToken(
                      userAccessToken,
                      item.workspaceId,
                      item.workspaceDomain,
                      item.userEmail,
                    );
                  }}
                  className="cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-800 p-2 recent-account-item rounded-lg"
                >
                  <div className="flex flex-row items-center">
                    <Avatar
                      avatarUrl={item.userAvatar}
                      name={item.userName}
                      size={48}
                    />
                    <div className={classNames("flex flex-col pl-2 flex-1")}>
                      <div className="name">
                        <span className="font-bold text-lg">
                          {item.workspaceName}
                        </span>{" "}
                        <span className="text-gray-600 dark:text-gray-500">
                          (domain: {item.workspaceDomain})
                        </span>
                      </div>
                      <div className="domain text-gray-600 dark:text-gray-300">
                        {item.userName}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>

          <div className="">Not seeing your workspace?</div>
          <a>Try a different email</a>
        </div>
        <Footer style={{ textAlign: "center" }}>
          ClickConnector ©{new Date().getFullYear()}
        </Footer>
      </Layout>
    );
  }

  if (step === "SELECT_ACCOUNT" && isLogginIn) {
    return (
      <Layout className="layout h-full w-full flex flex-col justify-center items-center">
        <img
          src="/cc-assets/logo.png"
          style={{ width: "5rem" }}
          alt="ClickConnector"
        />
        <div className="mt-4 bg-white dark:bg-gray-900 p-12 rounded-2xl text-center  mode_transition">
          <Title level={2}>Logging you in</Title>
        </div>
        <Footer style={{ textAlign: "center" }}>
          ClickConnector ©{new Date().getFullYear()}
        </Footer>
      </Layout>
    );
  }

  if (step === "SELECT_WORKSPACE") {
    return (
      <Layout className="layout h-full w-full flex flex-col justify-center items-center">
        <img
          src="/cc-assets/logo.png"
          style={{ width: "5rem" }}
          alt="ClickConnector"
        />
        <div className="mt-4 bg-white dark:bg-gray-900 p-12 rounded-2xl  mode_transition">
          <Title level={2}>Sign in to your workspace</Title>
          <div className="">Enter your workspace’s ClickConnector URL</div>
          <Form
            style={{ maxWidth: 400 }}
            onFinish={(values) => {
              validateWorkspace(values.domain);
            }}
          >
            <Form.Item
              name="domain"
              rules={[
                {
                  required: true,
                  message: "Please enter your workspace domain",
                },
              ]}
            >
              <Input
                prefix={<HomeOutlined className="site-form-item-icon" />}
                placeholder="Domain"
                size="large"
                addonAfter=".clickconnector.io"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="font-bold w-full"
                loading={isValidatingWorkspace}
                size="large"
                icon={<i className="ri-arrow-right-line"></i>}
              >
                Continue
              </Button>
            </Form.Item>
          </Form>

          <div className="flex flex-col">
            <div className="">
              Don’t know your workspace URL?
              <a className="pl-2" onClick={() => setStep("LOGIN")}>
                Login with email
              </a>
            </div>
            <div className="">
              Looking to create a workspace instead?{" "}
              <a
                className="pl-2"
                onClick={() => {
                  window.location.href = `https://register.clickconnector.io/`;
                }}
              >
                Create a new workspace
              </a>
            </div>
          </div>
        </div>
        <Footer style={{ textAlign: "center" }}>
          ClickConnector ©{new Date().getFullYear()}
        </Footer>
      </Layout>
    );
  }

  return (
    <Layout className="layout h-full w-full flex flex-col justify-center items-center">
      <img
        src="/cc-assets/logo.png"
        style={{ width: "5rem" }}
        alt="ClickConnector"
      />
      <div className="mt-4 bg-white dark:bg-gray-900 p-12 rounded-2xl  mode_transition">
        <Title level={3}>Welcome Back</Title>
        We suggest using the email address you use at work.
        <div className="mt-4">
          <SignInWithGoogle onClick={loginWithGoogle} />
          {/* <GoogleButton
            onClick={loginWithGoogle}
            type="dark"
            className="w-full font-bold"
          /> */}
        </div>
        <Divider>OR</Divider>
        <Form
          style={{ maxWidth: 400 }}
          onFinish={(values) => {
            getOtp(values.email);
          }}
        >
          <Form.Item
            name="email"
            rules={[{ required: true, message: "Please input your Email!" }]}
          >
            <Input
              prefix={<i className="ri-user-line"></i>}
              placeholder="Email"
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className="font-bold w-full"
              loading={isProcessing}
              size="large"
              icon={<i className="ri-arrow-right-line"></i>}
            >
              Sign in with Email
            </Button>
          </Form.Item>
          {recentLogins.length === 0 && (
            <Alert
              showIcon
              icon={<i className="ri-sparkling-line"></i>}
              description={
                <>
                  We’ll email you a magic code for a password-free sign in. Or
                  you can{" "}
                  <a onClick={() => setStep("SELECT_WORKSPACE")}>
                    sign in manually instead
                  </a>
                  .
                </>
              }
            ></Alert>
          )}
        </Form>
        {recentLogins.length > 0 && (
          <>
            <Divider>OR</Divider>
            <div className="mb-2 -mt-4 text-center">
              Login to a recent workspace
            </div>
            {recentLogins.map((item, index) => (
              <div
                key={index.toString()}
                onClick={() => switchProfile(item)}
                className="cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-800 p-2 recent-account-item"
              >
                <div className="flex flex-row items-center">
                  <Avatar
                    avatarUrl={item.avatar}
                    name={item.firstName}
                    size={48}
                  />
                  <div className={classNames("flex flex-col pl-2 flex-1")}>
                    <div className="email">{item.domain}</div>
                    <div className="domain text-gray-600"> {item.email}</div>
                  </div>
                  <div
                    className="delete-button"
                    onClick={(e) => {
                      removeProfile(item);
                      e.stopPropagation();
                    }}
                  >
                    <i className="ri-delete-bin-line"></i>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
      {recentLogins.length < 2 && (
        <Footer style={{ textAlign: "center" }}>
          ClickConnector ©{new Date().getFullYear()}
        </Footer>
      )}
    </Layout>
  );
};

export default SignInPage;
